/* global once */

/**
 * @file Member soft-gate behaviour (Protech SSO).
 *
 * Membership is signalled by the presence of the Protech cookie
 * (`cswe_protech_contact_id`, set by cswe_protech_sso). No Drupal session
 * exists, so the server ships one cached, locked variant to everyone and the
 * reveal happens client-side. The visual blur and the "Member Logout" link are
 * toggled pre-paint by CSS via the `is-member` class (html.html.twig); this
 * module handles only the parts that need JS.
 */

import { getCookie } from "./_helpers.js";

/**
 * Applies member/guest behaviour within a context.
 *
 * @param {HTMLElement|Document} context
 *   The scope to search (document on full page load, the inserted wrapper on
 *   Drupal AJAX updates). Element lookups are guarded with once() so repeat
 *   attaches are idempotent.
 */
export function memberGate(context) {
  if (getCookie("cswe_protech_contact_id")) {
    /* Members: lift the interaction block from soft-gated content. The blur is
       already removed pre-paint by CSS (is-member), and the utility-nav
       "Member Logout" link is revealed by CSS — no JS needed for either. */
    once("ts-cswe-members-only", ".members-only-content", context).forEach(
      (region) => region.removeAttribute("inert"),
    );
  } else {
    /* Guests: open the Protech sign-in flow in a popup. Also enhances any
       future sign-in links pointing at the Protech sign-in endpoint. */
    once(
      "ts-cswe-sso-login",
      'a[href*="membership.cswe.org/Security/Sign-In"]',
      context,
    ).forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        /* Return to this environment's SSO callback after Protech sign-in. */
        const url = new URL(link.href);
        url.searchParams.set(
          "returnurl",
          window.location.origin + "/sso/callback",
        );
        window.open(
          url.toString(),
          "sso_login",
          "width=560,height=680,scrollbars=yes,resizable=yes",
        );
      });
    });
  }
}
